import type { SchemaVersioned } from "./types";

export interface ActiveGameStoreOptions {
  readonly storageKey: string;
  readonly schemaVersion: number;
  // Game-specific shape check on the parsed blob — backgammon verifies
  // `parsed.turnState`, checkers verifies `Array.isArray(parsed.moves)`, etc.
  // Schema version + presence of an object are checked in the store; this
  // predicate only needs to assert the per-game fields exist.
  readonly isValid: (parsed: Record<string, unknown>) => boolean;
}

// Single-slot localStorage store for an in-progress game. Generic over the
// per-game `ActiveSession` shape so backgammon's TurnState-bearing session and
// checkers' move-list session use one class.
export class ActiveGameStore<TSession extends SchemaVersioned> {
  private readonly opts: ActiveGameStoreOptions;

  constructor(opts: ActiveGameStoreOptions) {
    this.opts = opts;
  }

  peek(): TSession | null {
    try {
      const raw = localStorage.getItem(this.opts.storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<TSession> | null;
      if (!parsed || typeof parsed !== "object") return null;
      if (parsed.version !== this.opts.schemaVersion) {
        // Schema mismatch — drop the stale slot rather than crash on load.
        this.tryRemove();
        return null;
      }
      if (!this.opts.isValid(parsed as Record<string, unknown>)) return null;
      return parsed as TSession;
    } catch {
      this.tryRemove();
      return null;
    }
  }

  save(session: TSession): void {
    try {
      localStorage.setItem(this.opts.storageKey, JSON.stringify(session));
    } catch {
      // Storage full / unavailable — silent. Resume just won't survive refresh.
    }
  }

  clear(): void {
    this.tryRemove();
  }

  private tryRemove(): void {
    try {
      localStorage.removeItem(this.opts.storageKey);
    } catch {
      // ignore
    }
  }
}
